import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Wrench, MapPin, Clock, ArrowRight } from "lucide-react";
import { MaskLine, Reveal } from "./Reveal";
import { IMAGES, CONTACT, SERVICE_POINTS } from "../../lib/data";
import { openKontaktTab } from "../../lib/scroll";

const FEATURES = [
  { icon: Wrench, title: "Erfaren serviceavdeling", text: "Dyktige teknikere med lang fartstid på storkjøkken- og vaskeriutstyr." },
  { icon: MapPin, title: "Hele Midt-Norge", text: "Personell strategisk plassert for rask utrykning i regionen." },
  { icon: Clock, title: "Rask respons", text: "Vi vet at driftsstans koster — vi prioriterer å få deg i gang igjen." },
];

export const Service = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const go = () => openKontaktTab("service");

  return (
    <section id="service" ref={ref} className="relative bg-ink text-white py-24 sm:py-32 overflow-hidden" data-testid="service-section">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-8 sm:gap-6 items-start">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <span className="font-oswald uppercase text-xs tracking-[0.3em] text-redx">Serviceavdeling</span>
            </Reveal>
            <h2 className="font-oswald font-bold uppercase tracking-tight leading-[0.85] text-5xl sm:text-7xl lg:text-8xl mt-4">
              <MaskLine>Service i</MaskLine>
              <MaskLine delay={0.1}>hele</MaskLine>
              <MaskLine delay={0.2} className="text-redx">Midt-Norge</MaskLine>
            </h2>

            <Reveal delay={0.15}>
              <div className="mt-8" data-testid="service-points">
                <p className="font-oswald uppercase text-xs tracking-[0.3em] text-white/50 mb-3">Servicepunkter</p>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_POINTS.map((p) => (
                    <span key={p} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-oswald uppercase text-sm tracking-widest text-white">
                      <MapPin size={14} className="text-redx" /> {p}
                    </span>
                  ))}
                </div>
                <p className="text-white/60 mt-4 max-w-md">Med servicepersonell fordelt over regionen sikrer vi rask respons og kort nedetid — og dekker <span className="text-white">hele Midt-Norge</span>.</p>
              </div>
            </Reveal>

            <div className="mt-12 space-y-8">
              {FEATURES.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.1}>
                  <div className="flex gap-5 border-t border-white/10 pt-6">
                    <f.icon className="text-redx shrink-0 mt-1" size={26} />
                    <div>
                      <h3 className="font-oswald uppercase text-xl tracking-tight">{f.title}</h3>
                      <p className="text-white/60 mt-1 max-w-md">{f.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <button
              onClick={go}
              data-testid="service-cta-button"
              className="group mt-12 flex items-center gap-3 bg-white text-ink px-7 py-4 font-oswald uppercase tracking-widest text-sm hover:bg-redx hover:text-white transition-colors duration-200"
            >
              Meld inn servicebehov <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <div className="relative h-[50vh] lg:h-[70vh] overflow-hidden">
              <motion.img
                src={IMAGES.serviceVan}
                alt="Storkjøkkensenteret Trøndelags servicebil med henger"
                style={{ y, objectPosition: "50% 68%" }}
                className="absolute inset-0 w-full h-[120%] object-cover"
              />
              <div className="absolute inset-0 border border-white/20" />
              <a
                href={`tel:+47${CONTACT.phoneRaw}`}
                data-testid="service-phone-badge"
                className="absolute top-5 left-5 bg-redx text-white px-5 py-3 font-oswald uppercase tracking-widest text-sm hover:bg-white hover:text-ink transition-colors"
              >
                Ring {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
